import React, { useEffect, useState } from "react";
import { notification } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { getTableInfo, getUserInfo, getToken } from "../../hooks/useHttp";
import { useTableSSE } from "../../hooks/useTableSSE";
import { useNavigate, useParams } from "react-router-dom";
import { baseUrl } from "../dasboard";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import {
  BoardHeader,
  BottomBar,
  CircleActionButton,
  ConnectionStatus,
  PixelButton,
  PixelConfirm,
  PixelModal,
  PlayerScoreRow,
  ScreenFrame,
} from "../../design/retro";

const revert = (data) => {
  data.sort(() => -1);
};
const timeConvert = (timestamp) => {
  return dayjs(new Date(timestamp)).format("HH:mm:ss, DD/MM");
};

function OverView() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState();
  const { tableId } = useParams();
  const tId = tableId || getTableInfo();
  const currentUser = getUserInfo();
  const [historyData, setHistoryData] = useState("");
  const [gameLogData, setGameLogData] = useState("");
  const [logData, setLogData] = useState("");
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [joinConfirmOpen, setJoinConfirmOpen] = useState(false);
  const { formatMessage } = useIntl();
  const [logModalTitle, setLogModalTitle] = useState();
  const token = getToken();

  // Use SSE hook for real-time updates
  const { tableData: sseTableData, isConnected, reconnect } = useTableSSE(tId, {
    enabled: !!tId,
  });

  // Update tableData when SSE receives new data
  useEffect(() => {
    if (sseTableData) {
      setTableData(sseTableData);
    }
  }, [sseTableData]);

  useEffect(() => {
    if (!tId) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tId]);

  const joinTable = async (tableId) => {
    if (currentUser && token) {
      try {
        const res = await fetch(
          `${baseUrl}/tables/${tableId}/players`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userId: currentUser.id }),
          }
        );

        if (!res.ok) {
          const data = await res.json();
          const errMess = data.error || data.message;

          if (res.status === 404) {
            navigate("/");
          }

          notification.error({
            message: errMess,
            placement: "top",
          });
        }
      } catch (error) {
        notification.error({
          message: "Failed to join table",
          placement: "top",
        });
      }
    } else {
      // Redirect to login if not authenticated
      notification.info({
        message: "Please log in to join a table",
        placement: "top",
      });
      navigate("/");
    }
  };

  const fetchTableData = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      // Add authorization header if token exists
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(`${baseUrl}/tables/${tId}`, {
        method: "GET",
        headers,
      });

      const data = await res.json();

      if (!res.ok) {
        const errMess = data.error || data.message;

        notification.error({
          message: errMess,
          placement: "top",
        });

        if (res.status === 404) {
          navigate("/");
        }
      }

      if (data) {
        const players = Object.keys(data.players).map(
          (item) => data.players[item]
        );
        const convertedData = players.map((item) => {
          return { ...item, key: item.id };
        });
        setTableData(convertedData);

        ////get log data
        if (data.history) {
          const history = Object.keys(data.history).map(
            (item) => data.history[item]
          );
          // console.log("history: ", history);
          setHistoryData(history);
          const convertedHistory = history.map((item) => {
            return { ...item, key: item.id };
          });

          const gameLog = convertedHistory.map((item, i) => {
            return ` ${i + 1}: ${item.fromPlayerId} ${formatMessage({
              id: "message.transferred",
            })} ${item.toPlayerId} ${item.amount} (${
              item.timestamp ? timeConvert(item?.timestamp) : ""
            })`;
          });

          setGameLogData(gameLog);
        }
      }
    } catch (error) {
      notification.error({
        message: "Failed to fetch table data",
        placement: "top",
      });
    }
  };

  // Initial fetch for history data (not covered by SSE)
  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPlayerLogData = (record, type) => {
    if (historyData) {
      const playerHistory = historyData?.filter((item, i) => {
        return item.fromPlayerId === record.id;
      });
      const incomingAmount = historyData?.filter((item, i) => {
        return item.toPlayerId === record.id;
      });

      const chickenHistory = historyData?.filter((item) => {
        return item.toPlayerId === "Chicken";
      });

      const chickenLog = chickenHistory.map((item, i) => {
        return `${i + 1}: ${item.toPlayerId.toUpperCase()} ${formatMessage({
          id: "message.transferred",
        })} ${item.fromPlayerId.toUpperCase()} ${item.amount} (${
          item.timestamp ? timeConvert(item?.timestamp) : ""
        })`;
      });

      const playerLog = playerHistory.map((item, i) => {
        return `${i + 1}: ${item.fromPlayerId.toUpperCase()} ${formatMessage({
          id: "message.transferred",
        })} ${item.toPlayerId.toUpperCase()} ${item.amount} (${
          item.timestamp ? timeConvert(item?.timestamp) : ""
        })`;
      });

      const incomingLog = incomingAmount.map((item, i) => {
        return `${i + 1}: ${item.toPlayerId.toUpperCase()} ${formatMessage({
          id: "message.received",
        })} ${item.amount} ${formatMessage({
          id: "message.from",
        })} ${item.fromPlayerId.toUpperCase()} (${
          item.timestamp ? timeConvert(item?.timestamp) : ""
        })`;
      });

      if (record.id === "Chicken") {
        revert(chickenLog);
        setLogData(chickenLog);
      }
      if (type === "out") {
        revert(playerLog);
        setLogData(playerLog);
      }
      if (type === "in") {
        if (incomingLog.length === 0) {
          setLogData(["Làm mà ăn đi bạn ơi =)))"]);
        } else {
          revert(incomingLog);
          setLogData(incomingLog);
        }
      }
    }
  };

  const t = (msgId, values) => formatMessage({ id: msgId }, values);

  const openLog = (player, type) => {
    const name = String(player.username || player.id).toUpperCase();
    setLogModalOpen(true);
    setLogModalTitle(`${t(type === "out" ? "modalTitle.outComingLog" : "modalTitle.incomingLog")} ${name}`);
    getPlayerLogData(player, type);
  };

  return (
    <>
      <ScreenFrame className="rt-board-screen">
        <BoardHeader
          left={
            <PixelButton variant="paper" size="sm" onClick={() => navigate("/")}>
              {t("button.backHome")}
            </PixelButton>
          }
          title={`#${String(tId || "").slice(0, 8)}`}
          status={<ConnectionStatus connected={isConnected} liveLabel={t("board.live")} offlineLabel={t("board.offline")} />}
          right={
            <PixelButton
              variant="paper"
              size="sm"
              onClick={() => {
                fetchTableData();
                if (!isConnected) reconnect();
              }}
            >
              {t("button.reload")}
            </PixelButton>
          }
        />

        <section className="rt-scoreboard-list" aria-label="Scoreboard">
          {tableData?.length ? (
            tableData.map((player) => (
              <PlayerScoreRow
                key={player.id}
                name={player.username || player.id}
                score={player.points ?? player.chips}
                isCurrentUser={currentUser?.id === player.id}
                youLabel={t("board.you")}
                extraActions={
                  <>
                    <CircleActionButton onClick={() => openLog(player, "out")} aria-label={t("board.outLog")}>
                      <ArrowUpOutlined />
                    </CircleActionButton>
                    <CircleActionButton onClick={() => openLog(player, "in")} aria-label={t("board.inLog")}>
                      <ArrowDownOutlined />
                    </CircleActionButton>
                  </>
                }
              />
            ))
          ) : (
            <p className="rt-empty">{t("ui.loading")}</p>
          )}
        </section>

        <BottomBar>
          <PixelButton
            variant="blue"
            onClick={() => {
              setLogModalOpen(true);
              setLogData(gameLogData);
              setLogModalTitle(t("board.tableLog"));
            }}
          >
            {t("board.tableLog")}
          </PixelButton>
          <PixelButton variant="primary" disabled={!token} onClick={() => setJoinConfirmOpen(true)}>
            {t("button.joinTable")}
          </PixelButton>
        </BottomBar>
      </ScreenFrame>

      <PixelConfirm
        open={joinConfirmOpen}
        title={t("button.joinTable")}
        message={t("board.joinConfirm")}
        confirmText={t("button.joinTable")}
        confirmVariant="primary"
        cancelText={t("button.cancel")}
        onCancel={() => setJoinConfirmOpen(false)}
        onConfirm={() => {
          setJoinConfirmOpen(false);
          joinTable(tId);
          navigate(`/${tId}`);
        }}
      />

      <PixelModal
        title={logModalTitle}
        open={logModalOpen}
        onClose={() => setLogModalOpen(false)}
        size="full"
        closeLabel={t("ui.close")}
      >
        {Array.isArray(logData) && logData.length > 0 ? (
          <div className="rt-card-list">
            {logData.map((item, index) => (
              <div className="rt-card rt-card-meta" key={`${item}-${index}`}>
                {item}
              </div>
            ))}
          </div>
        ) : (
          <p className="rt-empty">{t("board.noTransactions")}</p>
        )}
      </PixelModal>
    </>
  );
}

export default OverView;
